/**
 * ReferralLeaderboard.jsx — who referred the work, and what it pays
 * Browning Electric / ElecBid
 *
 * Ranks employees by the revenue their referred clients brought in. The tag is
 * the Jobber CLIENT custom field "Referred By"; api/jobber-sync.js
 * (syncReferralClients) resolves it to a canonical employee and stores it in
 * referral_clients. All the money logic lives in src/lib/referralPayout.js.
 *
 * Two things about this board that are easy to misread:
 *
 *   • EARNED is the payable number for the selected month — the job was billed
 *     in it. CARRIED FORWARD is work that is credited but still open; it moves
 *     to the next month, every month, until the job is actually invoiced.
 *     Carried dollars are shown so nobody thinks a referral was lost, and are
 *     deliberately excluded from the earned total.
 *
 *   • Dollar amounts follow the same rule as the review-bonus board: crew see
 *     the ranking and their own dollars; everyone else's are redacted unless
 *     they hold view_all_bonus_amounts.
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { apiFetch } from "../lib/apiFetch";
import {
  PAYOUT_BASES,
  buildReferralRows,
  leaderboardForMonth,
  activeMonths,
  monthKeyOf,
  monthLabel,
  shiftMonth,
} from "../lib/referralPayout";

// ── Brand colours (same palette as BonusTracker / ReferralBonuses) ────────────
const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1c2028", border: "#2a3140",
  accent: "#a5ce3a", accentDim: "rgba(165,206,58,0.12)",
  green: "#22c55e", greenDim: "rgba(34,197,94,0.10)",
  red: "#ef4444",
  yellow: "#eab308", yellowDim: "rgba(234,179,8,0.10)",
  blue: "#3b82f6", blueDim: "rgba(59,130,246,0.10)",
  orange: "#f97316", orangeDim: "rgba(249,115,22,0.12)",
  text: "#f0f4f8", muted: "#8892a4", dim: "#4a5568",
};

const MEDAL_COLORS = ["#FFD700", "#C0C0C0", "#CD7F32"];

const dollar = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const inputStyle = {
  padding: "6px 9px", borderRadius: 7, border: `1px solid ${C.border}`,
  background: C.card, color: C.text, fontSize: 12,
};

function btn(variant = "secondary", extra = {}) {
  const styles = {
    primary:   { background: C.accent,  color: "#0d0f12" },
    secondary: { background: C.surface, color: C.muted, border: `1px solid ${C.border}` },
  };
  return {
    padding: "6px 12px", borderRadius: 7, border: "none", cursor: "pointer",
    fontSize: 12, fontWeight: 600, ...(styles[variant] || styles.secondary), ...extra,
  };
}

// ── One job line inside an expanded employee ──────────────────────────────────
function JobLine({ row, showMoney, onEditAmount }) {
  const pending = !row.billedMonth;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, padding: "6px 10px",
      borderRadius: 7, background: C.bg, fontSize: 12,
    }}>
      <span style={{ color: C.dim, minWidth: 44, fontVariantNumeric: "tabular-nums" }}>#{row.jobNum}</span>
      <span style={{ flex: 1, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {row.clientName}
        {row.jobTitle ? <span style={{ color: C.dim }}> — {row.jobTitle}</span> : null}
      </span>
      {row.sharePct !== 100 && !row.overridden && (
        <span style={{ color: C.muted, fontSize: 11 }}>{row.sharePct}% share</span>
      )}
      {row.overridden && (
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
          color: C.blue, background: C.blueDim, whiteSpace: "nowrap",
        }} title={row.overrideNote || `Computed: ${dollar(row.computedAmount)}`}>
          edited
        </span>
      )}
      {row.paid && (
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
          color: C.green, background: C.greenDim, whiteSpace: "nowrap",
        }} title={row.paidMonth ? `Paid in ${monthLabel(row.paidMonth)}` : "Paid"}>
          ✓ paid
        </span>
      )}
      <span style={{
        fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
        color: pending ? C.orange : C.green,
        background: pending ? C.orangeDim : C.greenDim, whiteSpace: "nowrap",
      }}>
        {pending ? (row.derivedStatus === "active" ? "in progress" : "not billed") : "billed"}
      </span>
      <span style={{
        minWidth: 78, textAlign: "right", fontWeight: 700,
        color: row.projected ? C.muted : C.text, fontVariantNumeric: "tabular-nums",
      }}>
        {showMoney ? (row.amount == null ? "—" : `${row.projected ? "~" : ""}${dollar(row.amount)}`) : "—"}
      </span>
      {onEditAmount && (
        <button
          onClick={() => onEditAmount(row)}
          title="Set a custom amount for this referral (admins only)"
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: C.muted, fontSize: 12, padding: "0 2px", lineHeight: 1,
          }}
        >
          ✎
        </button>
      )}
    </div>
  );
}

export default function ReferralLeaderboard({
  jobs = [],
  invoices = [],
  techNames = [],
  canSeeBonusFor = () => true,
  isAdmin = false,
}) {
  const [tags, setTags]         = useState([]);
  const [rule, setRule]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [syncing, setSyncing]   = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [month, setMonth]       = useState(() => monthKeyOf(new Date()));

  const [overrides, setOverrides] = useState([]);

  const load = useCallback(async () => {
    setError(null);
    const [tagRes, ruleRes, ovRes] = await Promise.all([
      supabase.from("referral_clients").select("*"),
      supabase.from("referral_payout_settings").select("*"),
      supabase.from("referral_payout_overrides").select("*"),
    ]);
    if (tagRes.error) { setError(tagRes.error.message); setLoading(false); return; }
    setOverrides(ovRes.data || []);
    // ⚠️ Never fall back to a default rate here. RLS filters ROWS, so a crew
    // member denied this read would get [] with no error and silently see
    // different dollars than the owner for the same month. No rule = no board.
    if (ruleRes.error || !ruleRes.data?.length) {
      setError(ruleRes.error?.message ||
        "No referral payout rule found — an admin needs to set one before amounts can be shown.");
      setTags(tagRes.data || []);
      setLoading(false);
      return;
    }
    setTags(tagRes.data || []);
    setRule(ruleRes.data[0]);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(
    () => (rule ? buildReferralRows({ referralClients: tags, jobs, invoices, rule, overrides }) : []),
    [tags, jobs, invoices, rule, overrides]
  );

  const months = useMemo(() => activeMonths(rows, monthKeyOf(new Date())), [rows]);
  const board  = useMemo(
    () => leaderboardForMonth(rows, month, rule?.program_start),
    [rows, month, rule]
  );

  const unmatched = useMemo(
    () => tags.filter(t => !t.matched && t.referral_text),
    [tags]
  );

  const monthTotal = board.reduce((s, e) => s + e.earned, 0);
  const carryTotal = board.reduce((s, e) => s + e.carried, 0);
  const canSeeTotals = isAdmin || canSeeBonusFor("");

  // ── Refresh tags from Jobber ────────────────────────────────────────────────
  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await apiFetch("/api/jobber-sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "syncReferralClients" }),
      });
      if (!r.ok) throw new Error(`sync failed (${r.status})`);
      await load();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSyncing(false);
    }
  };

  // ── Save the payout rule ────────────────────────────────────────────────────
  const saveRule = async (patch) => {
    setSavingRule(true);
    const next = { ...rule, ...patch };
    const { error: err } = await supabase
      .from("referral_payout_settings")
      .update({
        basis: next.basis,
        rate: Number(next.rate) || 0,
        program_start: next.program_start || null,
        updated_at: new Date().toISOString(),
      })
      .eq("organization_id", rule.organization_id);
    setSavingRule(false);
    if (err) { setError(err.message); return; }
    setRule(next);
  };

  // ── Set / clear a custom amount on one (job × referrer) payout ─────────────
  // The custom amount is the employee's FINAL dollars for that referral — it
  // replaces the rule math and any share split. Blank restores the computed
  // number. Stored in referral_payout_overrides so the Bonus Tracker pays the
  // same figure this board shows.
  const editAmount = async (row) => {
    const current = row.overridden ? row.amount : row.computedAmount;
    const raw = window.prompt(
      `Custom referral amount for ${row.employee} — #${row.jobNum} ${row.clientName || ""}\n` +
      `Computed by the rule: ${row.computedAmount == null ? "—" : dollar(row.computedAmount)}\n` +
      `Leave blank to go back to the computed amount.`,
      current == null ? "" : String(Math.round(current * 100) / 100)
    );
    if (raw === null) return; // cancelled
    const trimmed = raw.trim().replace(/^\$/, "");
    const custom = trimmed === "" ? null : Number(trimmed);
    if (custom !== null && !isFinite(custom)) { setError(`"${raw}" is not a number`); return; }
    const { error: err } = await supabase
      .from("referral_payout_overrides")
      .upsert({
        job_num: String(row.jobNum),
        employee_name: row.employee,
        custom_amount: custom,
        updated_at: new Date().toISOString(),
      }, { onConflict: "organization_id,job_num,employee_name" });
    if (err) { setError(err.message); return; }
    await load();
  };

  // ── Reassign an unrecognised referral to an employee by hand ────────────────
  const assign = async (tag, name) => {
    const linked = name ? [{ name, share_pct: 100, manual: true }] : [];
    const { error: err } = await supabase
      .from("referral_clients")
      .update({
        linked_employees: linked,
        matched: linked.length > 0,
        manual_override: linked.length > 0,
        updated_at: new Date().toISOString(),
      })
      .eq("id", tag.id);
    if (err) { setError(err.message); return; }
    setTags(prev => prev.map(t => t.id === tag.id
      ? { ...t, linked_employees: linked, matched: linked.length > 0, manual_override: linked.length > 0 }
      : t));
  };

  if (loading) return <div style={{ color: C.muted, padding: 24 }}>Loading referrals…</div>;

  const basisMeta = PAYOUT_BASES.find(b => b.value === rule?.basis);
  const ruleText = !rule ? "—"
    : rule.basis === "flat_amount" ? `$${rule.rate} per job`
    : `${rule.rate}% of ${rule.basis === "profit_pct" ? "profit" : "revenue (ex-GST)"}`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* ── Header: month nav + what the rule is ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button onClick={() => setMonth(m => shiftMonth(m, -1))} style={btn("secondary")}>‹</button>
        <select value={month} onChange={e => setMonth(e.target.value)}
                style={{ ...inputStyle, cursor: "pointer", fontWeight: 700, minWidth: 170 }}>
          {(months.includes(month) ? months : [month, ...months]).map(m => (
            <option key={m} value={m}>{monthLabel(m)}</option>
          ))}
        </select>
        <button onClick={() => setMonth(m => shiftMonth(m, 1))} style={btn("secondary")}>›</button>

        <div style={{ flex: 1 }} />

        <span style={{ fontSize: 12, color: C.muted }}>
          Paying <strong style={{ color: C.accent }}>{ruleText}</strong>
        </span>
        <button onClick={handleSync} disabled={syncing} style={btn("secondary", { opacity: syncing ? 0.5 : 1 })}>
          {syncing ? "Refreshing…" : "Refresh from Jobber"}
        </button>
      </div>

      {error && (
        <div style={{ background: "rgba(239,68,68,0.10)", border: `1px solid ${C.red}40`,
                      borderRadius: 10, padding: "10px 14px", fontSize: 12, color: C.red }}>
          {error}
        </div>
      )}

      {/* ── Month totals ── */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {[
          { label: `Earned in ${monthLabel(month)}`, value: canSeeTotals ? dollar(monthTotal) : "—", color: C.accent,
            sub: `${board.reduce((s, e) => s + e.earnedJobs, 0)} job${board.reduce((s, e) => s + e.earnedJobs, 0) === 1 ? "" : "s"} billed` },
          { label: "Carried forward", value: canSeeTotals ? dollar(carryTotal) : "—", color: C.orange,
            sub: `${board.reduce((s, e) => s + e.carriedJobs, 0)} still open — rolls to next month` },
          { label: "Referring employees", value: String(board.length), color: C.text,
            sub: `${tags.filter(t => t.matched).length} tagged client${tags.filter(t => t.matched).length === 1 ? "" : "s"}` },
        ].map(card => (
          <div key={card.label} style={{
            flex: "1 1 190px", background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 12, padding: "12px 16px",
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: "0.08em" }}>
              {card.label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: card.color, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
              {card.value}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{card.sub}</div>
          </div>
        ))}
      </div>

      {/* ── The board ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {!board.length && (
          <div style={{ color: C.muted, fontSize: 13, padding: "20px 0", textAlign: "center" }}>
            No referral activity in {monthLabel(month)}.
          </div>
        )}
        {board.map((entry, i) => {
          const showMoney = canSeeBonusFor(entry.tech);
          const medal = i < 3 ? MEDAL_COLORS[i] : null;
          const open = expanded === entry.tech;
          return (
            <div key={entry.tech} style={{
              background: C.surface, borderRadius: 10,
              border: `1px solid ${medal ? `${medal}30` : C.border}`,
            }}>
              <div
                onClick={() => setExpanded(open ? null : entry.tech)}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", cursor: "pointer" }}
              >
                <span style={{
                  minWidth: 22, fontWeight: 800, fontSize: 14,
                  color: medal || C.dim, fontVariantNumeric: "tabular-nums",
                }}>{i + 1}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{entry.tech}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
                    {entry.earnedJobs} billed
                    {entry.carriedJobs > 0 && (
                      <span style={{ color: C.orange }}>
                        {" · "}{entry.carriedJobs} carried forward
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: C.accent, fontVariantNumeric: "tabular-nums" }}>
                    {showMoney ? dollar(entry.earned) : "—"}
                  </div>
                  {entry.carriedJobs > 0 && (
                    <div style={{ fontSize: 11, color: C.orange, fontVariantNumeric: "tabular-nums" }}>
                      {showMoney
                        ? `+${dollar(entry.carried)} pending${entry.carriedUnknown ? ` · ${entry.carriedUnknown} no est.` : ""}`
                        : "pending"}
                    </div>
                  )}
                </div>
                <span style={{ color: C.dim, fontSize: 12 }}>{open ? "▲" : "▼"}</span>
              </div>

              {open && (
                <div style={{ padding: "0 14px 12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
                  {entry.earnedRows.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                        Billed in {monthLabel(month)}
                      </div>
                      {entry.earnedRows.map(r => <JobLine key={`e${r.jobNum}`} row={r} showMoney={showMoney} onEditAmount={isAdmin ? editAmount : null} />)}
                    </div>
                  )}
                  {entry.carriedRows.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: C.orange, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                        Carried forward — pays when billed
                      </div>
                      {entry.carriedRows.map(r => <JobLine key={`c${r.jobNum}`} row={r} showMoney={showMoney} onEditAmount={isAdmin ? editAmount : null} />)}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Admin: the rule + referrals nobody on the roster matched ── */}
      {isAdmin && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 4 }}>

          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
              Payout rule
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <select
                value={rule?.basis || "revenue_pct"}
                onChange={e => saveRule({ basis: e.target.value })}
                disabled={savingRule}
                style={{ ...inputStyle, cursor: "pointer" }}
              >
                {PAYOUT_BASES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
              <input
                type="number" step="0.1" min="0"
                value={rule?.rate ?? ""}
                onChange={e => setRule(r => ({ ...r, rate: e.target.value }))}
                onBlur={e => saveRule({ rate: e.target.value })}
                disabled={savingRule}
                style={{ ...inputStyle, width: 80, textAlign: "right" }}
              />
              <span style={{ fontSize: 12, color: C.muted }}>{basisMeta?.suffix === "$" ? "dollars per job" : "percent"}</span>

              <div style={{ width: 1, height: 24, background: C.border }} />

              <span style={{ fontSize: 12, color: C.muted }}>Only count jobs billed on or after</span>
              <input
                type="date"
                value={rule?.program_start || ""}
                onChange={e => saveRule({ program_start: e.target.value || null })}
                disabled={savingRule}
                style={inputStyle}
              />
              {!rule?.program_start && (
                <span style={{ fontSize: 11, color: C.yellow }}>
                  unset — credits every job back to the client's first, including pre-programme work
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 8 }}>{basisMeta?.hint}</div>
          </div>

          {unmatched.length > 0 && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Referrals not matched to an employee
              </div>
              <div style={{ fontSize: 11, color: C.muted, margin: "4px 0 10px" }}>
                These clients have a “Referred By” value that isn’t anyone on the active roster — usually a customer
                referring a friend. Assign one if it should pay out.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {unmatched.map(t => (
                  <div key={t.id} style={{
                    display: "flex", alignItems: "center", gap: 10,
                    background: C.bg, borderRadius: 7, padding: "7px 10px", fontSize: 12,
                  }}>
                    <span style={{ flex: 1, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t.client_name}
                    </span>
                    <span style={{ color: C.muted, fontStyle: "italic" }}>“{t.referral_text}”</span>
                    <select
                      defaultValue=""
                      onChange={e => e.target.value && assign(t, e.target.value)}
                      style={{ ...inputStyle, cursor: "pointer", minWidth: 150 }}
                    >
                      <option value="">Assign to…</option>
                      {techNames.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
